import { useEffect, useMemo, useState } from "react";

type CodeData = {
	lines: string[];
	isLoading: boolean;
	error: string | null;
};

// Fetches raw file content from GitHub for a specific ref and path, returns sliced lines
export function useCodeSnippet(
	owner: string,
	repo: string,
	refspec: string,
	filePath: string,
	startLine: number,
	endLine: number | null
): CodeData {
	const [state, setState] = useState<CodeData>({ lines: [], isLoading: true, error: null });

	const apiUrl = useMemo(
		() =>
			owner && repo && refspec && filePath
				? `https://raw.githubusercontent.com/${owner}/${repo}/${encodeURIComponent(
						refspec
					)}/${filePath}`
				: "",
		[owner, repo, refspec, filePath]
	);

	useEffect(() => {
		if (!apiUrl) {
			setState({ lines: [], isLoading: false, error: null });
			return;
		}

		let cancelled = false;

		const fetchCode = async () => {
			try {
				setState((prev) => ({ ...prev, isLoading: true, error: null }));
				const res = await fetch(apiUrl, { headers: { Accept: "text/plain" } });
				if (!res.ok) {
					throw new Error(`Failed to fetch code: ${res.status}`);
				}
				const text = await res.text();
				if (cancelled) return;

				const allLines = text.split("\n");
				const startIdx = Math.max(1, startLine || 1) - 1;
				const endIdx = endLine && endLine > 0 ? endLine - 1 : startIdx;
				const slice = allLines.slice(startIdx, Math.min(endIdx + 1, allLines.length));

				setState({ lines: slice, isLoading: false, error: null });
			} catch (e) {
				if (cancelled) return;
				setState({
					lines: [],
					isLoading: false,
					error: e instanceof Error ? e.message : "Error",
				});
			}
		};

		void fetchCode();
		return () => {
			cancelled = true;
		};
	}, [apiUrl, startLine, endLine]);

	return state;
}
